import React, { useState, useEffect } from 'react';
import { AlertTriangle, Check, Loader2, Save, Trash2, X, Search, Settings } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { stcBinService } from '../../../../services/stcBinService';
import { stcBinSizeService } from '../../../../services/stcBinSizeService';
import { stcBinTypeService, StcBinTypeDto } from '../../../../services/stcBinTypeService';
import { stcBinGrpService, StcBinGrpDto } from '../../../../services/stcBinGrpService';
import { stcBinCategoryService, StcBinCategoryDto } from '../../../../services/stcBinCategoryService';
import { stcZoneService, StcZoneDto } from '../../../../services/stcZoneService';
import { stcPriorityService, StcPriorityDto } from '../../../../services/stcPriorityService';
import { stcResourceService, StcResourceDto } from '../../../../services/stcResourceService';
import { itemsService } from '../../../../services/itemsService';
import { ItemsDto } from '../../../../types/items';
import { StcBinSizeDto } from '../../../../types/stcBinSize';
import { BulkUpdateBinsRequest, BulkDeleteBinsRequest, FilterOptions } from './types';

interface BulkUpdateModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedBinCodes: string[];
    warehouseID: string;
    filterOptions: FilterOptions | null;
    onUpdateSuccess: () => void;
    onDeleteSuccess: () => void;
}

interface UpdateFields {
    binSizeNo: boolean;
    binTypeNo: boolean;
    resourceNo: boolean;
    binGrpNo: boolean;
    stcBinCatNo: boolean;
    stcZoneID: boolean;
    pickPriorityNo: boolean;
    pickableYesNo: boolean;
    reservationAllowed: boolean;
    autoReplenishment: boolean;
    itemNo: boolean;
    replenishMinLevel: boolean;
    replenishMaxLevel: boolean;
    customSortID: boolean;
    putAwayPriorityNo: boolean;
    negativeReservationAllowed: boolean;
    sharedBinYesNo: boolean;
    sharedBinMaxQuantity: boolean;
    sharedBinPreferEmptyBin: boolean;
}

type TabType = 'basics' | 'priority' | 'reservation' | 'replenishment' | 'shared';

/**
 * Bulk update modal with tabbed interface (5 tabs)
 * Replaces BulkUpdatePanel with a modern modal design
 */
export const BulkUpdateModal: React.FC<BulkUpdateModalProps> = ({
    isOpen,
    onClose,
    selectedBinCodes,
    warehouseID,
    filterOptions,
    onUpdateSuccess,
    onDeleteSuccess
}) => {
    const { user } = useAuth();
    const companyNo = user?.companyNo || '';

    const [activeTab, setActiveTab] = useState<TabType>('basics');

    // Real data from API
    const [binSizes, setBinSizes] = useState<StcBinSizeDto[]>([]);
    const [binTypes, setBinTypes] = useState<StcBinTypeDto[]>([]);
    const [binGroups, setBinGroups] = useState<StcBinGrpDto[]>([]);
    const [binCategories, setBinCategories] = useState<StcBinCategoryDto[]>([]);
    const [zones, setZones] = useState<StcZoneDto[]>([]);
    const [priorities, setPriorities] = useState<StcPriorityDto[]>([]);
    const [resources, setResources] = useState<StcResourceDto[]>([]);
    const [isLoadingBinSizes, setIsLoadingBinSizes] = useState(false);
    const [isLoadingBinTypes, setIsLoadingBinTypes] = useState(false);
    const [isLoadingBinGroups, setIsLoadingBinGroups] = useState(false);
    const [isLoadingBinCategories, setIsLoadingBinCategories] = useState(false);
    const [isLoadingZones, setIsLoadingZones] = useState(false);
    const [isLoadingPriorities, setIsLoadingPriorities] = useState(false);
    const [isLoadingResources, setIsLoadingResources] = useState(false);

    // Item search state
    const [itemNo, setItemNo] = useState<string>('');
    const [selectedItem, setSelectedItem] = useState<ItemsDto | null>(null);
    const [itemSearchTerm, setItemSearchTerm] = useState<string>('');
    const [itemSearchResults, setItemSearchResults] = useState<ItemsDto[]>([]);
    const [isSearchingItems, setIsSearchingItems] = useState(false);
    const [showItemDropdown, setShowItemDropdown] = useState(false);

    // Track which fields should be updated
    const [updateFields, setUpdateFields] = useState<UpdateFields>({
        binSizeNo: false,
        binTypeNo: false,
        resourceNo: false,
        binGrpNo: false,
        stcBinCatNo: false,
        stcZoneID: false,
        pickPriorityNo: false,
        pickableYesNo: false,
        reservationAllowed: false,
        autoReplenishment: false,
        itemNo: false,
        replenishMinLevel: false,
        replenishMaxLevel: false,
        customSortID: false,
        putAwayPriorityNo: false,
        negativeReservationAllowed: false,
        sharedBinYesNo: false,
        sharedBinMaxQuantity: false,
        sharedBinPreferEmptyBin: false
    });

    // Field values
    const [binSizeNo, setBinSizeNo] = useState<number>(1);
    const [binTypeNo, setBinTypeNo] = useState<number>(1);
    const [resourceNo, setResourceNo] = useState<number | undefined>(undefined);
    const [binGrpNo, setBinGrpNo] = useState<number | undefined>(undefined);
    const [stcBinCatNo, setStcBinCatNo] = useState<number | undefined>(undefined);
    const [stcZoneID, setStcZoneID] = useState<string>('001');
    const [pickPriorityNo, setPickPriorityNo] = useState<number | undefined>(50);
    const [pickableYesNo, setPickableYesNo] = useState<boolean>(true);
    const [reservationAllowed, setReservationAllowed] = useState<boolean>(true);
    const [autoReplenishment, setAutoReplenishment] = useState<boolean>(false);
    const [replenishMinLevel, setReplenishMinLevel] = useState<number>(0);
    const [replenishMaxLevel, setReplenishMaxLevel] = useState<number>(0);
    const [customSortID, setCustomSortID] = useState<number>(1000);
    const [putAwayPriorityNo, setPutAwayPriorityNo] = useState<number | undefined>(50);
    const [negativeReservationAllowed, setNegativeReservationAllowed] = useState<boolean>(false);
    const [sharedBinYesNo, setSharedBinYesNo] = useState<boolean>(false);
    const [sharedBinMaxQuantity, setSharedBinMaxQuantity] = useState<number>(0);
    const [sharedBinPreferEmptyBin, setSharedBinPreferEmptyBin] = useState<boolean>(false);

    // State
    const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [notification, setNotification] = useState<{
        type: 'success' | 'error' | 'warning';
        message: string;
    } | null>(null);

    // Load data on mount
    useEffect(() => {
        if (!isOpen || !companyNo) return;

        const loadData = async () => {
            // Load bin sizes
            setIsLoadingBinSizes(true);
            try {
                const sizes = await stcBinSizeService.getByCompanyNo(companyNo);
                setBinSizes(sizes);
                if (sizes.length > 0) {
                    setBinSizeNo(sizes[0].binSizeNo);
                }
            } catch (err) {
                console.error('Failed to load bin sizes:', err);
            } finally {
                setIsLoadingBinSizes(false);
            }

            // Load bin types
            setIsLoadingBinTypes(true);
            try {
                const types = await stcBinTypeService.getByCompanyNo(companyNo);
                setBinTypes(types);
                if (types.length > 0) {
                    setBinTypeNo(types[0].binTypeNo);
                }
            } catch (err) {
                console.error('Failed to load bin types:', err);
            } finally {
                setIsLoadingBinTypes(false);
            }

            // Load bin groups
            setIsLoadingBinGroups(true);
            try {
                const groups = await stcBinGrpService.getByCompanyNo(companyNo);
                setBinGroups(groups);
                if (groups.length > 0) {
                    setBinGrpNo(groups[0].binGrpNo);
                }
            } catch (err) {
                console.error('Failed to load bin groups:', err);
            } finally {
                setIsLoadingBinGroups(false);
            }

            // Load bin categories
            setIsLoadingBinCategories(true);
            try {
                const categories = await stcBinCategoryService.getByCompanyNo(companyNo);
                setBinCategories(categories);
                if (categories.length > 0) {
                    setStcBinCatNo(categories[0].stcBinCatNo);
                }
            } catch (err) {
                console.error('Failed to load bin categories:', err);
            } finally {
                setIsLoadingBinCategories(false);
            }

            // Load zones
            setIsLoadingZones(true);
            try {
                const zoneList = await stcZoneService.getByCompanyNo(companyNo);
                setZones(zoneList);
                if (zoneList.length > 0) {
                    setStcZoneID(zoneList[0].stcZoneID);
                }
            } catch (err) {
                console.error('Failed to load zones:', err);
            } finally {
                setIsLoadingZones(false);
            }

            // Load priorities
            setIsLoadingPriorities(true);
            try {
                const priorityList = await stcPriorityService.getByCompanyNo(companyNo);
                setPriorities(priorityList);
                if (priorityList.length > 0) {
                    setPickPriorityNo(priorityList[0].priorityNo);
                    setPutAwayPriorityNo(priorityList[0].priorityNo);
                }
            } catch (err) {
                console.error('Failed to load priorities:', err);
            } finally {
                setIsLoadingPriorities(false);
            }

            // Load resources
            setIsLoadingResources(true);
            try {
                const resourceList = await stcResourceService.getByCompanyNo(companyNo);
                setResources(resourceList);
                if (resourceList.length > 0) {
                    setResourceNo(resourceList[0].resourceNo);
                }
            } catch (err) {
                console.error('Failed to load resources:', err);
            } finally {
                setIsLoadingResources(false);
            }
        };

        loadData();
    }, [isOpen, companyNo]);

    // Search items with debounce
    useEffect(() => {
        const searchItems = async () => {
            if (!itemSearchTerm || itemSearchTerm.length < 2) {
                setItemSearchResults([]);
                setShowItemDropdown(false);
                return;
            }

            setIsSearchingItems(true);
            try {
                const results = await itemsService.searchCurrentItems(itemSearchTerm);
                setItemSearchResults(results);
                setShowItemDropdown(results.length > 0);
            } catch (err) {
                console.error('Failed to search items:', err);
                setItemSearchResults([]);
                setShowItemDropdown(false);
            } finally {
                setIsSearchingItems(false);
            }
        };

        const timeoutId = setTimeout(searchItems, 300);
        return () => clearTimeout(timeoutId);
    }, [itemSearchTerm]);

    // Reset fields when selection changes
    useEffect(() => {
        if (!isOpen) return;

        setUpdateFields({
            binSizeNo: false,
            binTypeNo: false,
            resourceNo: false,
            binGrpNo: false,
            stcBinCatNo: false,
            stcZoneID: false,
            pickPriorityNo: false,
            pickableYesNo: false,
            reservationAllowed: false,
            autoReplenishment: false,
            itemNo: false,
            replenishMinLevel: false,
            replenishMaxLevel: false,
            customSortID: false,
            putAwayPriorityNo: false,
            negativeReservationAllowed: false,
            sharedBinYesNo: false,
            sharedBinMaxQuantity: false,
            sharedBinPreferEmptyBin: false
        });
        setNotification(null);
        setActiveTab('basics');
    }, [selectedBinCodes, isOpen]);

    // Auto-hide notification after 5 seconds
    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => setNotification(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    // Handle item selection from dropdown
    const handleSelectItem = (item: ItemsDto) => {
        setSelectedItem(item);
        setItemNo(item.itemNo);
        setItemSearchTerm(item.itemNo);
        setShowItemDropdown(false);
    };

    // Clear selected item
    const handleClearItem = () => {
        setSelectedItem(null);
        setItemNo('');
        setItemSearchTerm('');
        setItemSearchResults([]);
        setShowItemDropdown(false);
    };

    // Check if at least one field is selected for update
    const hasSelectedFields = (): boolean => {
        return Object.values(updateFields).some(v => v);
    };

    // Build update request with only selected fields
    const buildUpdateRequest = (): BulkUpdateBinsRequest => {
        const request: BulkUpdateBinsRequest = {
            companyNo,
            warehouseID,
            binCodes: selectedBinCodes
        };

        if (updateFields.binSizeNo) request.binSizeNo = binSizeNo;
        if (updateFields.binTypeNo) request.binTypeNo = binTypeNo;
        if (updateFields.resourceNo) request.resourceNo = resourceNo;
        if (updateFields.binGrpNo) request.binGrpNo = binGrpNo;
        if (updateFields.stcBinCatNo) request.stcBinCatNo = stcBinCatNo;
        if (updateFields.stcZoneID) request.stcZoneID = stcZoneID;
        if (updateFields.pickPriorityNo) request.pickPriorityNo = pickPriorityNo;
        if (updateFields.pickableYesNo) request.pickableYesNo = pickableYesNo;
        if (updateFields.reservationAllowed) request.reservationAllowed = reservationAllowed;
        if (updateFields.autoReplenishment) request.autoReplenishment = autoReplenishment;
        if (updateFields.itemNo) request.itemNo = itemNo;
        if (updateFields.replenishMinLevel) request.replenishMinLevel = replenishMinLevel;
        if (updateFields.replenishMaxLevel) request.replenishMaxLevel = replenishMaxLevel;
        if (updateFields.customSortID) request.customSortID = customSortID;
        if (updateFields.putAwayPriorityNo) request.putAwayPriorityNo = putAwayPriorityNo;
        if (updateFields.negativeReservationAllowed) request.negativeReservationAllowed = negativeReservationAllowed;
        if (updateFields.sharedBinYesNo) request.sharedBinYesNo = sharedBinYesNo;
        if (updateFields.sharedBinMaxQuantity) request.sharedBinMaxQuantity = sharedBinMaxQuantity;
        if (updateFields.sharedBinPreferEmptyBin) request.sharedBinPreferEmptyBin = sharedBinPreferEmptyBin;

        return request;
    };

    // Get descriptions for display
    const getBinSizeDescription = (sizeNo: number): string => {
        const size = binSizes.find(s => s.binSizeNo === sizeNo);
        return size ? `${sizeNo} - ${size.description1}` : `${sizeNo}`;
    };

    const getBinTypeDescription = (typeNo: number): string => {
        const type = binTypes.find(t => t.binTypeNo === typeNo);
        return type ? `${typeNo} - ${type.description1 || 'Bin Type'}` : `${typeNo}`;
    };

    const getBinGrpDescription = (grpNo: number): string => {
        const group = binGroups.find(g => g.binGrpNo === grpNo);
        return group ? `${grpNo} - ${group.description1}` : `${grpNo}`;
    };

    const getBinCategoryDescription = (catNo: number): string => {
        const category = binCategories.find(c => c.stcBinCatNo === catNo);
        return category ? `${catNo} - ${category.lng_1}` : `${catNo}`;
    };

    const getZoneDescription = (zoneID: string): string => {
        const zone = zones.find(z => z.stcZoneID === zoneID);
        return zone ? `${zoneID} - ${zone.description1}` : zoneID;
    };

    const getPriorityDescription = (priorityNo: number): string => {
        const priority = priorities.find(p => p.priorityNo === priorityNo);
        return priority ? `${priorityNo} - ${priority.description1}` : `${priorityNo}`;
    };

    const getResourceDescription = (resourceNo: number): string => {
        const resource = resources.find(r => r.resourceNo === resourceNo);
        return resource ? `${resourceNo} - ${resource.description1}` : `${resourceNo}`;
    };

    // Get summary of fields that will be updated
    const getUpdateSummary = (): string[] => {
        const summary: string[] = [];

        if (updateFields.binSizeNo) summary.push(`Bin Storrelse: ${getBinSizeDescription(binSizeNo)}`);
        if (updateFields.binTypeNo) summary.push(`Bin Type: ${getBinTypeDescription(binTypeNo)}`);
        if (updateFields.resourceNo) summary.push(`Ressurs: ${resourceNo ? getResourceDescription(resourceNo) : 'Ingen'}`);
        if (updateFields.binGrpNo) summary.push(`Bin Gruppe: ${binGrpNo ? getBinGrpDescription(binGrpNo) : 'Ingen'}`);
        if (updateFields.stcBinCatNo) summary.push(`Lokasjonskategori: ${stcBinCatNo ? getBinCategoryDescription(stcBinCatNo) : 'Ingen'}`);
        if (updateFields.stcZoneID) summary.push(`Sone: ${getZoneDescription(stcZoneID)}`);
        if (updateFields.pickPriorityNo) summary.push(`Plukk Prioritet: ${pickPriorityNo ? getPriorityDescription(pickPriorityNo) : 'Ingen'}`);
        if (updateFields.pickableYesNo) summary.push(`Plukkbar: ${pickableYesNo ? 'Ja' : 'Nei'}`);
        if (updateFields.reservationAllowed) summary.push(`Reservasjon: ${reservationAllowed ? 'Ja' : 'Nei'}`);
        if (updateFields.autoReplenishment) summary.push(`Auto-pafylling: ${autoReplenishment ? 'Ja' : 'Nei'}`);
        if (updateFields.itemNo) {
            const itemDescription = selectedItem ? `${itemNo} - ${selectedItem.description1}` : itemNo;
            summary.push(`Varenummer: ${itemDescription}`);
        }
        if (updateFields.replenishMinLevel) summary.push(`Min pafyllniva: ${replenishMinLevel}`);
        if (updateFields.replenishMaxLevel) summary.push(`Maks pafyllniva: ${replenishMaxLevel}`);
        if (updateFields.customSortID) summary.push(`Sortering ID: ${customSortID}`);
        if (updateFields.putAwayPriorityNo) summary.push(`Put-away Prioritet: ${putAwayPriorityNo ? getPriorityDescription(putAwayPriorityNo) : 'Ingen'}`);
        if (updateFields.negativeReservationAllowed) summary.push(`Negativ reservasjon: ${negativeReservationAllowed ? 'Ja' : 'Nei'}`);
        if (updateFields.sharedBinYesNo) summary.push(`Delt lokasjon: ${sharedBinYesNo ? 'Ja' : 'Nei'}`);
        if (updateFields.sharedBinMaxQuantity) summary.push(`Maks antall delt: ${sharedBinMaxQuantity}`);
        if (updateFields.sharedBinPreferEmptyBin) summary.push(`Foretrekk tom lokasjon: ${sharedBinPreferEmptyBin ? 'Ja' : 'Nei'}`);

        return summary;
    };

    // Handle update operation
    const handleUpdate = async () => {
        if (!hasSelectedFields()) {
            setNotification({
                type: 'error',
                message: 'Du ma velge minst ett felt som skal oppdateres'
            });
            return;
        }

        setShowUpdateConfirm(true);
    };

    // Confirm and execute update
    const executeUpdate = async () => {
        setIsUpdating(true);
        setShowUpdateConfirm(false);
        setNotification(null);

        try {
            const request = buildUpdateRequest();
            const response = await stcBinService.bulkUpdateBins(request);

            if (response.errors.length > 0) {
                setNotification({
                    type: 'warning',
                    message: `Oppdatert ${response.successCount} av ${selectedBinCodes.length} lokasjoner. ${response.errors.length} feil oppstod.`
                });
            } else {
                setNotification({
                    type: 'success',
                    message: `${response.successCount} lokasjon(er) oppdatert`
                });
            }

            // Refresh and clear selection
            onUpdateSuccess();
        } catch (err) {
            setNotification({
                type: 'error',
                message: err instanceof Error ? err.message : 'Oppdatering feilet'
            });
        } finally {
            setIsUpdating(false);
        }
    };

    // Handle delete operation
    const handleDelete = () => {
        setShowDeleteConfirm(true);
    };

    // Confirm and execute delete
    const executeDelete = async () => {
        setIsDeleting(true);
        setShowDeleteConfirm(false);
        setNotification(null);

        try {
            const request: BulkDeleteBinsRequest = {
                companyNo,
                warehouseID,
                binCodes: selectedBinCodes,
                forceDelete: false
            };

            const response = await stcBinService.bulkDeleteBins(request);

            if (response.errors.length > 0) {
                setNotification({
                    type: 'warning',
                    message: `Slettet ${response.successCount} av ${selectedBinCodes.length} lokasjoner. ${response.errors.length} kunne ikke slettes (trolig med saldo).`
                });
            } else {
                setNotification({
                    type: 'success',
                    message: `${response.successCount} lokasjon(er) slettet`
                });
            }

            // Refresh and clear selection
            onDeleteSuccess();
        } catch (err) {
            setNotification({
                type: 'error',
                message: err instanceof Error ? err.message : 'Sletting feilet'
            });
        } finally {
            setIsDeleting(false);
        }
    };

    if (!isOpen) return null;

    const tabs: { id: TabType; label: string }[] = [
        { id: 'basics', label: 'Grunnleggende' },
        { id: 'priority', label: 'Prioritet & Plukk' },
        { id: 'reservation', label: 'Reservasjon' },
        { id: 'replenishment', label: 'Påfylling' },
        { id: 'shared', label: 'Delt lokasjon' }
    ];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <Settings className="w-6 h-6 text-blue-600" />
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Masseoppdatering</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                {selectedBinCodes.length} lokasjon(er) valgt
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Notification */}
                {notification && (
                    <div className={`mx-6 mt-4 rounded-lg p-3 flex items-start space-x-2 ${
                        notification.type === 'success' ? 'bg-green-50 border border-green-200' :
                        notification.type === 'error' ? 'bg-red-50 border border-red-200' :
                        'bg-yellow-50 border border-yellow-200'
                    }`}>
                        {notification.type === 'success' && <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />}
                        {notification.type === 'error' && <X className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />}
                        {notification.type === 'warning' && <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />}
                        <p className={`text-sm flex-1 ${
                            notification.type === 'success' ? 'text-green-800' :
                            notification.type === 'error' ? 'text-red-800' :
                            'text-yellow-800'
                        }`}>
                            {notification.message}
                        </p>
                        <button
                            onClick={() => setNotification(null)}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* Tab Navigation */}
                <div className="border-b border-gray-200 px-6">
                    <div className="flex space-x-8">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                                    activeTab === tab.id
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tab Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="space-y-4">
                        {/* Tab 1: Grunnleggende (Basics) */}
                        {activeTab === 'basics' && (
                            <>
                                {/* BinSizeNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.binSizeNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, binSizeNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Bin Storrelse</span>
                                    </label>
                                    <select
                                        value={binSizeNo}
                                        onChange={(e) => setBinSizeNo(parseInt(e.target.value))}
                                        disabled={!updateFields.binSizeNo || isLoadingBinSizes}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        {isLoadingBinSizes ? (
                                            <option>Laster...</option>
                                        ) : binSizes.length > 0 ? (
                                            binSizes.map(size => (
                                                <option key={size.binSizeNo} value={size.binSizeNo}>
                                                    {size.binSizeNo} - {size.description1}
                                                </option>
                                            ))
                                        ) : filterOptions?.binSizeNo && filterOptions.binSizeNo.length > 0 ? (
                                            filterOptions.binSizeNo.map(value => (
                                                <option key={value} value={value}>{value}</option>
                                            ))
                                        ) : (
                                            <option value={binSizeNo}>{binSizeNo}</option>
                                        )}
                                    </select>
                                </div>

                                {/* BinTypeNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.binTypeNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, binTypeNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Bin Type</span>
                                    </label>
                                    <select
                                        value={binTypeNo}
                                        onChange={(e) => setBinTypeNo(parseInt(e.target.value))}
                                        disabled={!updateFields.binTypeNo || isLoadingBinTypes}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        {isLoadingBinTypes ? (
                                            <option>Laster...</option>
                                        ) : binTypes.length > 0 ? (
                                            binTypes.map(type => (
                                                <option key={type.binTypeNo} value={type.binTypeNo}>
                                                    {type.binTypeNo} - {type.description1 || 'Bin Type'}
                                                </option>
                                            ))
                                        ) : filterOptions?.binTypeNo && filterOptions.binTypeNo.length > 0 ? (
                                            filterOptions.binTypeNo.map(value => (
                                                <option key={value} value={value}>{value}</option>
                                            ))
                                        ) : (
                                            <option value={binTypeNo}>{binTypeNo}</option>
                                        )}
                                    </select>
                                </div>

                                {/* ResourceNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.resourceNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, resourceNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Ressurs</span>
                                    </label>
                                    <select
                                        value={resourceNo || ''}
                                        onChange={(e) => setResourceNo(e.target.value ? parseInt(e.target.value) : undefined)}
                                        disabled={!updateFields.resourceNo || isLoadingResources}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        <option value="">Ingen</option>
                                        {isLoadingResources ? (
                                            <option>Laster...</option>
                                        ) : resources.length > 0 ? (
                                            resources.map(resource => (
                                                <option key={resource.resourceNo} value={resource.resourceNo}>
                                                    {resource.resourceNo} - {resource.description1}
                                                </option>
                                            ))
                                        ) : filterOptions?.resourceNo && filterOptions.resourceNo.length > 0 ? (
                                            filterOptions.resourceNo.map(value => (
                                                <option key={value} value={value}>{value}</option>
                                            ))
                                        ) : null}
                                    </select>
                                </div>

                                {/* BinGrpNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.binGrpNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, binGrpNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Bin Gruppe</span>
                                    </label>
                                    <select
                                        value={binGrpNo || ''}
                                        onChange={(e) => setBinGrpNo(e.target.value ? parseInt(e.target.value) : undefined)}
                                        disabled={!updateFields.binGrpNo || isLoadingBinGroups}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        <option value="">Ingen</option>
                                        {isLoadingBinGroups ? (
                                            <option>Laster...</option>
                                        ) : binGroups.length > 0 ? (
                                            binGroups.map(group => (
                                                <option key={group.binGrpNo} value={group.binGrpNo}>
                                                    {group.binGrpNo} - {group.description1}
                                                </option>
                                            ))
                                        ) : filterOptions?.binGrpNo && filterOptions.binGrpNo.length > 0 ? (
                                            filterOptions.binGrpNo.map(value => (
                                                <option key={value} value={value}>{value}</option>
                                            ))
                                        ) : null}
                                    </select>
                                </div>

                                {/* StcBinCatNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.stcBinCatNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, stcBinCatNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Lokasjonskategori</span>
                                    </label>
                                    <select
                                        value={stcBinCatNo || ''}
                                        onChange={(e) => setStcBinCatNo(e.target.value ? parseInt(e.target.value) : undefined)}
                                        disabled={!updateFields.stcBinCatNo || isLoadingBinCategories}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        <option value="">Ingen</option>
                                        {isLoadingBinCategories ? (
                                            <option>Laster...</option>
                                        ) : binCategories.length > 0 ? (
                                            binCategories.map(category => (
                                                <option key={category.stcBinCatNo} value={category.stcBinCatNo}>
                                                    {category.stcBinCatNo} - {category.lng_1}
                                                </option>
                                            ))
                                        ) : filterOptions?.stcBinCatNo && filterOptions.stcBinCatNo.length > 0 ? (
                                            filterOptions.stcBinCatNo.map(value => (
                                                <option key={value} value={value}>{value}</option>
                                            ))
                                        ) : null}
                                    </select>
                                </div>

                                {/* StcZoneID */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.stcZoneID}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, stcZoneID: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Sone</span>
                                    </label>
                                    <select
                                        value={stcZoneID}
                                        onChange={(e) => setStcZoneID(e.target.value)}
                                        disabled={!updateFields.stcZoneID || isLoadingZones}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        {isLoadingZones ? (
                                            <option>Laster...</option>
                                        ) : zones.length > 0 ? (
                                            zones.map(zone => (
                                                <option key={zone.stcZoneID} value={zone.stcZoneID}>
                                                    {zone.stcZoneID} - {zone.description1}
                                                </option>
                                            ))
                                        ) : filterOptions?.stcZoneID && filterOptions.stcZoneID.length > 0 ? (
                                            filterOptions.stcZoneID.map(value => (
                                                <option key={value} value={value}>{value}</option>
                                            ))
                                        ) : (
                                            <option value={stcZoneID}>{stcZoneID}</option>
                                        )}
                                    </select>
                                </div>
                            </>
                        )}

                        {/* Tab 2: Prioritet & Plukk (Priority & Picking) */}
                        {activeTab === 'priority' && (
                            <>
                                {/* PickPriorityNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.pickPriorityNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, pickPriorityNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Plukk Prioritet</span>
                                    </label>
                                    <select
                                        value={pickPriorityNo || ''}
                                        onChange={(e) => setPickPriorityNo(e.target.value ? parseInt(e.target.value) : undefined)}
                                        disabled={!updateFields.pickPriorityNo || isLoadingPriorities}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        <option value="">Ingen</option>
                                        {isLoadingPriorities ? (
                                            <option>Laster...</option>
                                        ) : priorities.length > 0 ? (
                                            priorities.map(priority => (
                                                <option key={priority.priorityNo} value={priority.priorityNo}>
                                                    {priority.priorityNo} - {priority.description1}
                                                </option>
                                            ))
                                        ) : filterOptions?.pickPriorityNo && filterOptions.pickPriorityNo.length > 0 ? (
                                            filterOptions.pickPriorityNo.map(value => (
                                                <option key={value} value={value}>{value}</option>
                                            ))
                                        ) : null}
                                    </select>
                                </div>

                                {/* PutAwayPriorityNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.putAwayPriorityNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, putAwayPriorityNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Put-away Prioritet</span>
                                    </label>
                                    <select
                                        value={putAwayPriorityNo || ''}
                                        onChange={(e) => setPutAwayPriorityNo(e.target.value ? parseInt(e.target.value) : undefined)}
                                        disabled={!updateFields.putAwayPriorityNo || isLoadingPriorities}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        <option value="">Ingen</option>
                                        {isLoadingPriorities ? (
                                            <option>Laster...</option>
                                        ) : priorities.length > 0 ? (
                                            priorities.map(priority => (
                                                <option key={priority.priorityNo} value={priority.priorityNo}>
                                                    {priority.priorityNo} - {priority.description1}
                                                </option>
                                            ))
                                        ) : null}
                                    </select>
                                </div>

                                {/* PickableYesNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.pickableYesNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, pickableYesNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Plukkbar</span>
                                    </label>
                                    <label className="flex items-center space-x-3 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                                        <input
                                            type="checkbox"
                                            checked={pickableYesNo}
                                            onChange={(e) => setPickableYesNo(e.target.checked)}
                                            disabled={!updateFields.pickableYesNo}
                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-30"
                                        />
                                        <span className="text-sm text-gray-700">Aktiver plukkbar</span>
                                    </label>
                                </div>

                                {/* CustomSortID */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.customSortID}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, customSortID: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Sortering ID</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={customSortID}
                                        onChange={(e) => setCustomSortID(parseInt(e.target.value) || 1000)}
                                        disabled={!updateFields.customSortID}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    />
                                </div>
                            </>
                        )}

                        {/* Tab 3: Reservasjon (Reservation) */}
                        {activeTab === 'reservation' && (
                            <>
                                {/* ReservationAllowed */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.reservationAllowed}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, reservationAllowed: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Reservasjon</span>
                                    </label>
                                    <label className="flex items-center space-x-3 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                                        <input
                                            type="checkbox"
                                            checked={reservationAllowed}
                                            onChange={(e) => setReservationAllowed(e.target.checked)}
                                            disabled={!updateFields.reservationAllowed}
                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-30"
                                        />
                                        <span className="text-sm text-gray-700">Tillat reservasjon</span>
                                    </label>
                                </div>

                                {/* NegativeReservationAllowed */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.negativeReservationAllowed}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, negativeReservationAllowed: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Negativ reservasjon</span>
                                    </label>
                                    <label className="flex items-center space-x-3 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                                        <input
                                            type="checkbox"
                                            checked={negativeReservationAllowed}
                                            onChange={(e) => setNegativeReservationAllowed(e.target.checked)}
                                            disabled={!updateFields.negativeReservationAllowed}
                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-30"
                                        />
                                        <span className="text-sm text-gray-700">Tillat negativ reservasjon</span>
                                    </label>
                                </div>
                            </>
                        )}

                        {/* Tab 4: Påfylling (Replenishment) */}
                        {activeTab === 'replenishment' && (
                            <>
                                {/* AutoReplenishment */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.autoReplenishment}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, autoReplenishment: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Auto-pafylling</span>
                                    </label>
                                    <label className="flex items-center space-x-3 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                                        <input
                                            type="checkbox"
                                            checked={autoReplenishment}
                                            onChange={(e) => setAutoReplenishment(e.target.checked)}
                                            disabled={!updateFields.autoReplenishment}
                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-30"
                                        />
                                        <span className="text-sm text-gray-700">Aktiver auto-pafylling</span>
                                    </label>
                                </div>

                                {/* ReplenishMinLevel */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.replenishMinLevel}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, replenishMinLevel: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Min pafyllniva</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={replenishMinLevel}
                                        onChange={(e) => setReplenishMinLevel(parseFloat(e.target.value) || 0)}
                                        disabled={!updateFields.replenishMinLevel}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    />
                                </div>

                                {/* ReplenishMaxLevel */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.replenishMaxLevel}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, replenishMaxLevel: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Maks pafyllniva</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={replenishMaxLevel}
                                        onChange={(e) => setReplenishMaxLevel(parseFloat(e.target.value) || 0)}
                                        disabled={!updateFields.replenishMaxLevel}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    />
                                </div>

                                {/* ItemNo - WITH SEARCH! */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.itemNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, itemNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Varenummer</span>
                                    </label>
                                    <div className="relative">
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={itemSearchTerm}
                                                onChange={(e) => setItemSearchTerm(e.target.value)}
                                                onFocus={() => {
                                                    if (itemSearchResults.length > 0) {
                                                        setShowItemDropdown(true);
                                                    }
                                                }}
                                                disabled={!updateFields.itemNo}
                                                placeholder="Sok varenummer, beskrivelse eller GTIN..."
                                                className="w-full px-3 py-2 pr-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                            />
                                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                                {isSearchingItems ? (
                                                    <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                                                ) : selectedItem ? (
                                                    <button
                                                        type="button"
                                                        onClick={handleClearItem}
                                                        className="pointer-events-auto text-gray-400 hover:text-gray-600"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                ) : (
                                                    <Search className="w-4 h-4 text-gray-400" />
                                                )}
                                            </div>
                                        </div>

                                        {/* Search results dropdown */}
                                        {showItemDropdown && itemSearchResults.length > 0 && (
                                            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                {itemSearchResults.map((item) => (
                                                    <button
                                                        key={item.itemNo}
                                                        type="button"
                                                        onClick={() => handleSelectItem(item)}
                                                        className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                                                    >
                                                        <div className="font-medium text-gray-900">{item.itemNo}</div>
                                                        <div className="text-xs text-gray-600 truncate">{item.description1}</div>
                                                        {item.itemBarCode && (
                                                            <div className="text-xs text-gray-500">GTIN: {item.itemBarCode}</div>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        {/* Selected item display */}
                                        {selectedItem && (
                                            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                                                <div className="font-medium text-blue-900">{selectedItem.itemNo}</div>
                                                <div className="text-blue-700">{selectedItem.description1}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Tab 5: Delt lokasjon (Shared Location) */}
                        {activeTab === 'shared' && (
                            <>
                                {/* SharedBinYesNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.sharedBinYesNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, sharedBinYesNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Delt lokasjon</span>
                                    </label>
                                    <label className="flex items-center space-x-3 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                                        <input
                                            type="checkbox"
                                            checked={sharedBinYesNo}
                                            onChange={(e) => setSharedBinYesNo(e.target.checked)}
                                            disabled={!updateFields.sharedBinYesNo}
                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-30"
                                        />
                                        <span className="text-sm text-gray-700">Aktiver delt lokasjon</span>
                                    </label>
                                </div>

                                {/* SharedBinMaxQuantity */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.sharedBinMaxQuantity}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, sharedBinMaxQuantity: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Maks antall delt</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={sharedBinMaxQuantity}
                                        onChange={(e) => setSharedBinMaxQuantity(parseInt(e.target.value) || 0)}
                                        disabled={!updateFields.sharedBinMaxQuantity}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    />
                                </div>

                                {/* SharedBinPreferEmptyBin */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.sharedBinPreferEmptyBin}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, sharedBinPreferEmptyBin: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Foretrekk tom lokasjon</span>
                                    </label>
                                    <label className="flex items-center space-x-3 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                                        <input
                                            type="checkbox"
                                            checked={sharedBinPreferEmptyBin}
                                            onChange={(e) => setSharedBinPreferEmptyBin(e.target.checked)}
                                            disabled={!updateFields.sharedBinPreferEmptyBin}
                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-30"
                                        />
                                        <span className="text-sm text-gray-700">Foretrekk tomme lokasjoner</span>
                                    </label>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-6 bg-gray-50">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                            {selectedBinCodes.length} lokasjoner valgt
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Avbryt
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={isUpdating || isDeleting}
                                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isDeleting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Trash2 className="w-4 h-4" />
                                )}
                                <span>Slett valgte</span>
                            </button>
                            <button
                                onClick={handleUpdate}
                                disabled={!hasSelectedFields() || isUpdating || isDeleting}
                                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isUpdating ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4" />
                                )}
                                <span>Oppdater</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Update confirmation dialog */}
            {showUpdateConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
                        <div className="p-6 space-y-4">
                            <div className="flex items-start space-x-3">
                                <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-gray-900">Bekreft oppdatering</h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                        Du er i ferd med a oppdatere {selectedBinCodes.length} lokasjon(er)
                                    </p>
                                </div>
                            </div>

                            {/* Fields to update */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <p className="text-sm font-medium text-blue-900 mb-2">Felt som vil bli oppdatert:</p>
                                <ul className="text-sm text-blue-800 space-y-1">
                                    {getUpdateSummary().map((item, idx) => (
                                        <li key={idx}>• {item}</li>
                                    ))}
                                </ul>
                            </div>

                            {/* BinCodes list */}
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                <p className="text-sm font-medium text-gray-900 mb-2">Lokasjoner:</p>
                                <div className="max-h-40 overflow-y-auto">
                                    <div className="text-xs text-gray-700 space-y-1">
                                        {selectedBinCodes.slice(0, 20).map((code, idx) => (
                                            <div key={idx} className="font-mono">{code}</div>
                                        ))}
                                        {selectedBinCodes.length > 20 && (
                                            <div className="text-gray-500 italic">
                                                ... og {selectedBinCodes.length - 20} flere
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
                            <button
                                onClick={() => setShowUpdateConfirm(false)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Avbryt
                            </button>
                            <button
                                onClick={executeUpdate}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Bekreft oppdatering
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete confirmation dialog */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
                        <div className="p-6 space-y-4">
                            <div className="flex items-start space-x-3">
                                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-gray-900">Bekreft sletting</h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                        Du er i ferd med a slette {selectedBinCodes.length} lokasjon(er)
                                    </p>
                                </div>
                            </div>

                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <p className="text-sm text-red-800">
                                    <strong>Advarsel:</strong> Denne handlingen kan ikke angres. Lokasjoner med saldo vil ikke bli slettet.
                                </p>
                            </div>

                            {/* BinCodes list */}
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                <p className="text-sm font-medium text-gray-900 mb-2">Lokasjoner som vil slettes:</p>
                                <div className="max-h-40 overflow-y-auto">
                                    <div className="text-xs text-gray-700 space-y-1">
                                        {selectedBinCodes.slice(0, 20).map((code, idx) => (
                                            <div key={idx} className="font-mono">{code}</div>
                                        ))}
                                        {selectedBinCodes.length > 20 && (
                                            <div className="text-gray-500 italic">
                                                ... og {selectedBinCodes.length - 20} flere
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Avbryt
                            </button>
                            <button
                                onClick={executeDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                                Ja, slett
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
